import { FirebaseFirestore } from '@capacitor-firebase/firestore';
import { configureFirestore } from 'features/firebase/configureFirestore';
import { getAppLocale } from 'features/i18n/utils/getAppLocale';
import { logUnlessPageLeft } from 'shared/utils/pageLeave';
import type { DbUser } from '@hub/common';

type EnsureUserDataArgs = {
  uid: string;
  tokenLocale?: string | null | undefined;
};

const pendingCreates = new Set<string>();

const readUserData = async (uid: string): Promise<DbUser | null> => {
  const { snapshot } = await FirebaseFirestore.getDocument<DbUser>({
    reference: `userData/${uid}`,
  });

  return snapshot.data;
};

const createUserData = async (uid: string, language: string): Promise<void> => {
  try {
    await FirebaseFirestore.setDocument({
      reference: `userData/${uid}`,
      data: { id: uid, language } satisfies DbUser,
    });
  } catch (error) {
    const createdElsewhere = await readUserData(uid).catch(() => null);

    if (createdElsewhere === null) {
      logUnlessPageLeft('User record could not be created', error);
    }
  }
};

const startCreate = (uid: string, language: string): void => {
  if (pendingCreates.has(uid)) {
    return;
  }

  pendingCreates.add(uid);
  void createUserData(uid, language).finally(() => {
    pendingCreates.delete(uid);
  });
};

/**
 * Reads the participant's `userData/{uid}` record and resolves to the
 * participant's language. A missing record is created as `{ id, language }`
 * in the background: the write is issued before this resolves, so later
 * writes to the record from this tab queue behind it.
 */
export async function ensureUserData({
  uid,
  tokenLocale,
}: EnsureUserDataArgs): Promise<string> {
  await configureFirestore();
  const userData = await readUserData(uid);
  const language = userData?.language ?? getAppLocale(tokenLocale ?? undefined);

  if (userData === null) {
    startCreate(uid, language);
  }

  return language;
}
